import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { getIcon } from "../lib/icons";
import { useCart } from "../lib/CartContext";
import { Minus, Plus, ShoppingCart, Star } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "../lib/i18n";

const TABS = [
  { key: "all" },
  { key: "dog", filter: (p) => p.pet_type === "dog" || p.pet_type === "both" },
  { key: "cat", filter: (p) => p.pet_type === "cat" || p.pet_type === "both" },
  { key: "Food", filter: (p) => /Food/i.test(p.category) },
  { key: "Toys", filter: (p) => /Toys/i.test(p.category) },
  { key: "Treats", filter: (p) => /Treats/i.test(p.category) },
  { key: "Grooming", filter: (p) => /Grooming/i.test(p.category) },
  { key: "Litter", filter: (p) => /Litter/i.test(p.category) },
  { key: "Accessories", filter: (p) => /(Collars|Beds|Bowls|Travel)/i.test(p.category) },
  { key: "Health", filter: (p) => /Health/i.test(p.category) },
];

export default function ProductGrid({ activeCategory, onCategoryClear }) {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("all");
  const { addItem } = useCart();
  const { t } = useLang();
  const [qtys, setQtys] = useState({});

  useEffect(() => {
    let alive = true;
    api
      .get("/products")
      .then((r) => alive && setProducts(r.data || []))
      .catch(() => alive && setProducts([]))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, []);

  const filtered = useMemo(() => {
    let list = [...products];
    if (activeCategory) list = list.filter((p) => p.category === activeCategory);
    const tabDef = TABS.find((x) => x.key === tab);
    if (tabDef && tabDef.filter) list = list.filter(tabDef.filter);
    return list;
  }, [products, tab, activeCategory]);

  const setQty = (id, q) => setQtys((s) => ({ ...s, [id]: Math.max(1, Math.min(99, q)) }));

  return (
    <section id="products" className="py-16 sm:py-20" style={{ background: "#FFF7E6" }}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
          <div>
            <span className="text-xs font-semibold tracking-[0.2em] text-slate-500">{t("products.tag")}</span>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold mt-2">
              {t("products.title_pre")} <span style={{ color: "var(--pm-cat)" }}>{t("products.title_mid")}</span>{" "}
              {t("products.title_post")}
            </h2>
            {activeCategory && (
              <button
                onClick={onCategoryClear}
                className="mt-2 text-sm font-semibold underline"
                data-testid="clear-category-filter"
              >
                {t("products.clear_filter")}: {activeCategory} ✕
              </button>
            )}
          </div>
        </div>

        <div className="flex flex-wrap gap-2 mb-8" data-testid="product-tabs">
          {TABS.map((tt) => (
            <button
              key={tt.key}
              onClick={() => setTab(tt.key)}
              data-testid={`tab-${tt.key}`}
              className={`neo-btn px-4 py-2 text-sm ${tab === tt.key ? "text-white" : "bg-white text-slate-900"}`}
              style={tab === tt.key ? { background: "var(--pm-dog)" } : {}}
            >
              {t(`products.tabs.${tt.key}`)}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="text-center py-20 text-slate-500" data-testid="products-loading">{t("products.loading")}</div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-20 text-slate-500" data-testid="products-empty">
            {t("products.empty")}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5" data-testid="products-grid">
            {filtered.map((p) => {
              const Icon = getIcon(p.icon);
              const q = qtys[p.id] || 1;
              const imgSrc = p.image_url ? `${process.env.REACT_APP_BACKEND_URL}${p.image_url}` : null;
              // Stock display logic — falls back to legacy in_stock when stock is not set
              const hasStock = typeof p.stock === "number";
              const stockNum = hasStock ? p.stock : null;
              const isPurchasable = hasStock ? stockNum > 0 : !!p.in_stock;
              let badgeText, badgeBg, urgencyText = null;
              if (hasStock) {
                if (stockNum === 0) {
                  badgeText = t("products.out_of_stock");
                  badgeBg = "#F87171";
                } else if (stockNum <= 3) {
                  badgeText = t("products.only_left", { n: stockNum });
                  badgeBg = "#FB923C";
                  urgencyText = t("products.urgency_almost", { n: stockNum });
                } else if (stockNum <= 10) {
                  badgeText = t("products.low_stock", { n: stockNum });
                  badgeBg = "#FBBF24";
                  urgencyText = t("products.urgency_limited", { n: stockNum });
                } else {
                  badgeText = t("products.available");
                  badgeBg = "#A3E635";
                }
              } else {
                badgeText = p.in_stock ? t("products.in_stock") : t("products.out");
                badgeBg = p.in_stock ? "#A3E635" : "#F87171";
              }
              return (
                <div key={p.id} className="neo-card p-4 flex flex-col" data-testid={`product-card-${p.id}`}>
                  <Link to={`/products/${p.slug || p.id}`} data-testid={`product-link-${p.id}`}>
                    <div
                      className="w-full aspect-square rounded-xl flex items-center justify-center overflow-hidden"
                      style={{ background: p.color, border: "2px solid #0F172A" }}
                    >
                      {imgSrc ? (
                        <img
                          src={imgSrc}
                          alt={p.name}
                          loading="lazy"
                          className="w-full h-full object-contain p-3"
                          data-testid={`product-img-${p.id}`}
                        />
                      ) : (
                        <Icon size={88} className="text-white" strokeWidth={1.5} />
                      )}
                    </div>
                  </Link>
                  <div className="mt-4 flex items-start justify-between gap-2">
                    <Link to={`/products/${p.slug || p.id}`} className="min-w-0">
                      <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">
                        {t(`categories.labels.${p.category}`)}
                      </div>
                      <h3 className="font-display text-lg font-semibold leading-tight hover:underline">{p.name}</h3>
                    </Link>
                    <span
                      className="text-xs font-bold px-2 py-1 rounded-md whitespace-nowrap"
                      style={{
                        background: badgeBg,
                        color: "#0F172A",
                        border: "2px solid #0F172A",
                      }}
                      data-testid={`stock-${p.id}`}
                    >
                      {badgeText}
                    </span>
                  </div>
                  {p.review_count > 0 && (
                    <div className="mt-1 flex items-center gap-1" data-testid={`product-rating-${p.id}`}>
                      {[1, 2, 3, 4, 5].map((i) => (
                        <Star
                          key={i}
                          size={13}
                          className={i <= Math.round(p.average_rating) ? "text-yellow-400" : "text-slate-300"}
                          fill={i <= Math.round(p.average_rating) ? "currentColor" : "none"}
                        />
                      ))}
                      <span className="text-xs text-slate-500">({p.review_count})</span>
                    </div>
                  )}
                  <p className="text-sm text-slate-600 mt-1 line-clamp-2">{p.description}</p>
                  {urgencyText && (
                    <div
                      className="mt-2 text-xs font-bold"
                      style={{ color: stockNum <= 3 ? "#DC2626" : "#B45309" }}
                      data-testid={`urgency-${p.id}`}
                    >
                      {urgencyText}
                    </div>
                  )}
                  <div className="mt-3 flex items-center justify-between">
                    <div className="font-display text-2xl font-bold">
                      {p.price.toFixed(0)} <span className="text-sm">EGP</span>
                    </div>
                    <div className="inline-flex items-center neo-flat overflow-hidden">
                      <button onClick={() => setQty(p.id, q - 1)} className="px-2 py-1" data-testid={`qty-dec-${p.id}`}>
                        <Minus size={14} />
                      </button>
                      <span className="px-2 text-sm font-semibold" data-testid={`qty-val-${p.id}`}>{q}</span>
                      <button onClick={() => setQty(p.id, q + 1)} className="px-2 py-1" data-testid={`qty-inc-${p.id}`}>
                        <Plus size={14} />
                      </button>
                    </div>
                  </div>
                  <button
                    disabled={!isPurchasable}
                    onClick={() => {
                      addItem(p, q);
                      toast.success(`${p.name} ${t("cart.added")}`);
                    }}
                    className="neo-btn mt-3 px-4 py-2 text-sm flex items-center justify-center gap-2 text-white disabled:opacity-50"
                    style={{ background: "var(--pm-dog)" }}
                    data-testid={`add-to-cart-${p.id}`}
                  >
                    <ShoppingCart size={16} /> {t("products.add_to_cart")}
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
}
